import { ChangeEvent, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { addYears } from 'date-fns';
import validator from 'validator';

import { authenticationAPIService } from '~/api/login';
import { qrcodeAPIService } from '~/api/qrcode';
import { CookieNames } from '~/constants/cookie';
import { HTTP_STATUSE_CODES, HTTP_STATUSES } from '~/constants/http-statuses-constants';
import { templates } from '~/constants/queryParams';
import { PATH } from '~/constants/route-path';
import { USER_EMAIL, USER_ID } from '~/constants/storages';
import { cookieService } from '~/services/cookie-service';
import { visitorAttributionService } from '~/services/visitorAttribution';
import { decodeJWT } from '~/utils/decodeJwt';
import { logger } from '~/utils/logger';

type useLoginProps = {
  cameFromPopup?: boolean;
  openConfirmationPopup?: () => void;
  selectedQR?: number;
  qrCode?: { id: number };
  isAnonCreateQr?: boolean;
};

export const useLogin = ({
  cameFromPopup,
  openConfirmationPopup,
  selectedQR,
  qrCode,
  isAnonCreateQr,
}: useLoginProps) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [, setCookie] = useCookies([CookieNames.userEmail]);

  const [email, setEmail] = useState('');
  const [isError, setIsError] = useState<boolean | string>(false);
  const [googleToken, setGoogleToken] = useState<{ access_token: string } | undefined>(undefined);
  const [facebookToken, setFacebookToken] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');

  const getNextPage = (): string => {
    const next = searchParams.get('next');
    if (next) {
      return next;
    }
    if (cameFromPopup) {
      return `${PATH.QR.CREATE}?template=${templates[selectedQR]}`;
    }
    return PATH.QR.MAIN;
  };

  const login = async (): Promise<void> => {
    try {
      const res = await authenticationAPIService.loginViaMagicLink({
        email,
        cf_turnstile_token: turnstileToken,
        ...(selectedQR && { microsite_id: selectedQR }),
        ...(qrCode?.id && { microsite_type: qrCode?.id }),
      });

      if (res.code === HTTP_STATUSES.BAD_REQUEST) {
        setIsError(t('alerts:login'));
        return;
      }

      if (res?.data?.refresh || res?.data?.access) {
        const { refresh, access, ...other } = res?.data || {};
        cookieService.setGroupOfCookies(other);

        if (access) {
          cookieService.setCookie('access', access, 365);
        }
        if (refresh) {
          cookieService.setCookie('refresh', refresh, 365);
        }

        const _email = res.data.email || email;
        const decodedToken = decodeJWT(res.data.access);
        const decodedUserId = decodedToken?.user_id;

        if (_email) {
          if (decodedToken?.user_id) {
            localStorage.setItem(USER_ID, String(decodedToken.user_id));
          }
          localStorage.setItem(USER_EMAIL, _email);
          setCookie(CookieNames.userEmail, _email, {
            path: '/',
            sameSite: 'lax',
            expires: addYears(new Date(), 1),
          });
        }

        axios.defaults.headers.common['Authorization'] = `Bearer ${res?.data?.access}`;

        window.dataLayer?.push({
          event: 'sign_up',
          user_id: decodedUserId,
        });

        visitorAttributionService.clearVisitorID();

        if (isAnonCreateQr) {
          await qrcodeAPIService.linkQrCodeToUser(qrCode?.id, qrCode);
          navigate(PATH.QR.MAIN);
          return;
        } else {
          navigate(getNextPage());
          return;
        }
      }

      if (cameFromPopup && openConfirmationPopup) {
        openConfirmationPopup();
        return;
      }

      navigate(PATH.CONFIRMATION);
    } catch (e) {
      setIsError(t('alerts:login'));
      logger.error(e);
    }
  };

  const handleEmailValue = (e: ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;
    const isEmail = validator.isEmail(value, { allow_utf8_local_part: false });
    setEmail(value);
    setIsEmailValid(!isEmail);

    if (!isEmail) {
      setIsError(false);
    }
  };

  const loadAccessGoogleToken = async (): Promise<void> => {
    try {
      const res = await authenticationAPIService.getAccessTokenFromGoogle({
        access_token: googleToken?.access_token!,
        code: '',
        id_token: '',
      });

      if (res?.status === HTTP_STATUSE_CODES.OK) {
        const { refresh, access, ...other } = res?.data || {};
        cookieService.setGroupOfCookies(other);

        if (access) {
          cookieService.setCookie('access', access, 365);
        }
        if (refresh) {
          cookieService.setCookie('refresh', refresh, 365);
        }

        if (res.data.user?.email) {
          localStorage.setItem(USER_ID, String(res.data.user.pk));
          localStorage.setItem(USER_EMAIL, res.data.user.email);
          setCookie(CookieNames.userEmail, res.data.user.email, {
            path: '/',
            sameSite: 'lax',
            expires: addYears(new Date(), 1),
          });
        }

        axios.defaults.headers.common['Authorization'] = `Bearer ${res?.data?.access}`;

        if (res.data.isRegistration) {
          window.dataLayer?.push({
            event: 'sign_up',
            user_id: res.data?.user && String(res.data.user?.pk),
          });
          // Clear visitor ID after successful registration
          visitorAttributionService.clearVisitorID();
        } else {
          window.dataLayer?.push({
            event: 'sign_in',
            user_id: res.data?.user && String(res.data.user?.pk),
          });
        }

        if (isAnonCreateQr) {
          await qrcodeAPIService.linkQrCodeToUser(qrCode?.id, qrCode);
          navigate(PATH.QR.MAIN);
        } else {
          navigate(getNextPage());
        }
      }
    } catch {
      setIsError(t('alerts:socialError'));
    }
  };

  useEffect(() => {
    if (googleToken?.access_token) {
      loadAccessGoogleToken();
    }
  }, [googleToken, facebookToken]);

  return {
    handleEmailValue,
    email,
    login,
    turnstileToken,
    isError,
    isEmailValid,
    setGoogleToken,
    setFacebookToken,
    setTurnstileToken,
    setIsError,
  };
};
